import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Box, Button, Container, Tabs, Tab, Typography, makeStyles } from '@material-ui/core'
import { Form as FinalForm, FormSpy } from 'react-final-form'
import DocumentRequire from '../component/um/settingSystem/DocumentRequire'
import DocumentSetForm from '../component/um/settingSystem/DocumentSetForm'
import DocumentDeadlineForm from '../component/um/settingSystem/DocumentDeadlineForm'
import {
    SubmittedAdditionalDetail,
    SubmittedAdditionalRequest,
    SubmittedAdditionalTab,
    useSettingSystem,
} from '../services/um/useSettingSystem'
import useLoading from '../control/loading/useLoading'
import useTranslate from '../i18n/useTranslate'
import Dialog, { DialogType } from '../control/dialog/dialog'
import { FormApi } from 'final-form'

const considerTypeMap: Record<number, 'fullBoard' | 'expedite' | 'exempt'> = {
    1: 'fullBoard',
    2: 'expedite',
    3: 'exempt',
}

const fieldToConsiderTypeId: Record<'fullBoard' | 'expedite' | 'exempt', number> = {
    fullBoard: 1,
    expedite: 2,
    exempt: 3,
}

const toInitialValues = (data: SubmittedAdditionalDetail[]) => {
    const initialValues: Record<string, { fullBoard: any; expedite: any; exempt: any }> = {}

    data.forEach((tab) => {
        initialValues[tab.submittedAdditionalTab] = { fullBoard: null, expedite: null, exempt: null }
        tab.items.forEach((item) => {
            const key = considerTypeMap[item.considerTypeID]
            if (key) {
                let val: any = item.value
                if (typeof val === 'boolean') val = val ? '1' : '0'
                initialValues[tab.submittedAdditionalTab][key] = val
            }
        })
    })
    return initialValues
}

const formatDataToSave = (tabKey: string, values: { fullBoard: any; expedite: any; exempt: any }) => ({
    submittedAdditionalTab: tabKey,
    items: Object.entries(values).map(([field, value]) => ({
        considerTypeID: fieldToConsiderTypeId[field as keyof typeof fieldToConsiderTypeId],
        value: Number(value),
    })),
})

const SettingDeadlinePage: React.FC = () => {
    const classes = useStyles()
    const loadingHook = useLoading()
    const t = useTranslate()
    const { getSubmittedAdditionalSetting, updateSubmittedAdditionalSetting } = useSettingSystem()

    const [tabIndex, setTabIndex] = React.useState(0)
    const [initialValues, setInitialValues] = useState<any>({})
    const [isDirty, setIsDirty] = useState(false)
    const formRef = React.useRef<FormApi<any> | null>(null)

    const [configDialog, setConfigDialog] =
        useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogRef = useRef<Record<string, Function>>()
    const onRefDialog = (fn: Record<string, Function>) => (dialogRef.current = fn)

    const tabs = [
        {
            label: t.get('UM.SETTING_SYSTEM.TAB.REQUIRE_DOC'),
            key: SubmittedAdditionalTab.isRequestCopy,
            Component: DocumentRequire,
        },
        {
            label: t.get('UM.SETTING_SYSTEM.TAB.DOC_SET'),
            key: SubmittedAdditionalTab.copies,
            Component: DocumentSetForm,
        },
        {
            label: t.get('UM.SETTING_SYSTEM.TAB.DOC_DEADLINE'),
            key: SubmittedAdditionalTab.extensionDays,
            Component: DocumentDeadlineForm,
        },
    ]

    const fetchData = async () => {
        loadingHook.loadingOpen()
        try {
            const res = await getSubmittedAdditionalSetting()
            if (res) {
                const next = toInitialValues(res)
                setInitialValues(next)
                const keyNow = tabs[tabIndex].key
                formRef.current?.restart(next[keyNow] || {})
            }
        } finally {
            loadingHook.loadingClose()
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const openMustSaveDialog = useCallback(() => {
        setConfigDialog({
            title: t.get('ALERT.TITLE.NOTIFICATION'),
            content: t.get('ALERT.CONTENT.NEED_TO_SAVE_FIRST'),
            type: 'alert-message',
            onAfterClose: () => setConfigDialog(undefined),
        })
        dialogRef.current?.openDialog()
    }, [t])

    const handleChangeTab = (_e: React.ChangeEvent<{}>, nextIndex: number) => {
        if (isDirty) {
            openMustSaveDialog()
            return
        }
        setTabIndex(nextIndex)
    }

    const onSubmit = async (values: any) => {
        loadingHook.loadingOpen()
        try {
            const activeKey = tabs[tabIndex].key
            const payload = formatDataToSave(activeKey, values)
            await updateSubmittedAdditionalSetting(payload as SubmittedAdditionalRequest)
            await fetchData()
            setIsDirty(false)

            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'alert-message',
                onAfterClose: () => setConfigDialog(undefined),
            })
            dialogRef.current?.openDialog()
        } finally {
            loadingHook.loadingClose()
        }
    }

    const ActiveComponent = tabs[tabIndex].Component
    const activeKey = tabs[tabIndex].key

    return (
        <Container maxWidth="lg">
            <Typography variant="h5" className={classes.header}>
                {t.get('UM.SETTING_SYSTEM.HEADER')}
            </Typography>

            <Tabs value={tabIndex} onChange={handleChangeTab} className={classes.tabRoot} indicatorColor="primary">
                {tabs.map((tab) => (
                    <Tab key={tab.key} label={tab.label} />
                ))}
            </Tabs>

            <FinalForm
                key={String(activeKey)}
                onSubmit={onSubmit}
                initialValues={initialValues[activeKey] || {}}
                render={({ handleSubmit, form }) => {
                    formRef.current = form
                    return (
                        <>
                            <FormSpy subscription={{ dirty: true }} onChange={({ dirty }) => setIsDirty(!!dirty)} />

                            <ActiveComponent />

                            <Box className={classes.btnGroup}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                        form.restart(initialValues[activeKey] || {})
                                    }}
                                >
                                    {t.get('BUTTON.CANCEL')}
                                </Button>

                                <Button variant="contained" color="primary" onClick={handleSubmit}>
                                    {t.get('BUTTON.SAVE_2')}
                                </Button>
                            </Box>
                        </>
                    )
                }}
            />

            <Dialog onRef={onRefDialog} {...configDialog} />
        </Container>
    )
}

const useStyles = makeStyles((theme) => ({
    header: { fontWeight: 700, marginBottom: theme.spacing(2) },
    tabRoot: { borderBottom: `1px solid ${theme.palette.divider}`, marginBottom: theme.spacing(3) },
    btnGroup: { display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(2), marginTop: theme.spacing(3) },
}))

export default SettingDeadlinePage
